import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, CircularProgress, Rating
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { suppliersAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const SupplierForm = ({ open, onClose, supplier, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  useEffect(() => { reset(supplier || { payment_terms: 30, currency: 'USD', rating: 5 }); }, [supplier, open, reset]);
  const onSubmit = async (data) => {
    try { setLoading(true);
      if (supplier?.id) { await suppliersAPI.update(supplier.id, data); toast.success('Supplier updated'); }
      else { await suppliersAPI.create(data); toast.success('Supplier created'); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{supplier?.id ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {[
              { name: 'name', label: 'Company Name', required: true, xs: 12, sm: 6 },
              { name: 'contact_person', label: 'Contact Person', xs: 12, sm: 6 },
              { name: 'email', label: 'Email', xs: 12, sm: 6 },
              { name: 'phone', label: 'Phone', xs: 12, sm: 6 },
              { name: 'address', label: 'Address', xs: 12 },
              { name: 'city', label: 'City', xs: 6, sm: 4 },
              { name: 'country', label: 'Country', xs: 6, sm: 4 },
              { name: 'tax_number', label: 'Tax Number', xs: 12, sm: 4 },
              { name: 'payment_terms', label: 'Payment Terms (days)', type: 'number', xs: 6, sm: 3 },
              { name: 'currency', label: 'Currency', xs: 6, sm: 3 },
              { name: 'bank_name', label: 'Bank Name', xs: 12, sm: 6 },
              { name: 'bank_account', label: 'Bank Account', xs: 12, sm: 6 },
            ].map(({ name, label, required, type, xs, sm }) => (
              <Grid item xs={xs} sm={sm} key={name}>
                <TextField fullWidth label={label} type={type||'text'}
                  {...register(name, required ? { required: true } : {})}
                  error={!!errors[name]} />
              </Grid>
            ))}
            <Grid item xs={12}><TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : (supplier?.id ? 'Update' : 'Create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]); const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0); const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(''); const [formOpen, setFormOpen] = useState(false); const [editSupplier, setEditSupplier] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try { setLoading(true);
      const { data } = await suppliersAPI.getAll({ page: page+1, limit: rowsPerPage, search: search||undefined });
      setSuppliers(data.data||[]); setTotal(data.pagination?.total||0);
    } catch { toast.error('Failed to load suppliers'); } finally { setLoading(false); }
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const columns = [
    { key: 'supplier_code', label: 'Code', render: (v) => <Typography fontFamily="monospace" fontWeight={600} fontSize="0.8rem">{v}</Typography> },
    { key: 'name', label: 'Supplier', render: (v) => <Typography fontWeight={600}>{v}</Typography> },
    { key: 'contact_person', label: 'Contact' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'country', label: 'Country' },
    { key: 'total_orders', label: 'Orders', align: 'right' },
    { key: 'total_purchased', label: 'Total Purchased', align: 'right', render: (v) => fmt(v) },
    { key: 'rating', label: 'Rating', render: (v) => <Rating value={v} readOnly size="small" /> },
    { key: 'is_active', label: 'Status', render: (v) => <StatusChip status={v ? 'active' : 'inactive'} /> },
    { key: 'id', label: '', render: (v, row) => (
      <Button size="small" onClick={() => { setEditSupplier(row); setFormOpen(true); }}>Edit</Button>
    )},
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Suppliers</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditSupplier(null); setFormOpen(true); }}>Add Supplier</Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <TextField placeholder="Search suppliers..." size="small" sx={{ minWidth: 300 }}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>
      <DataTable columns={columns} rows={suppliers} loading={loading} total={total} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
      <SupplierForm open={formOpen} onClose={() => setFormOpen(false)} supplier={editSupplier} onSaved={fetchSuppliers} />
    </Box>
  );
};

export default SuppliersPage;
